import { useSyncExternalStore } from 'react';
import type { LastCallDB, OutboxEntry, Shadow } from '@/data/db';
import { deepEqual, merge3, mergeRecord } from './merge';
import { isSyncedTable, MAX_PUSH, normalizeCode, randomKey, SYNCED_TABLES, TABLES_VERSION, type ChangeIn, type PullResponse, type PushResponse } from './protocol';

/**
 * This device's side of sharing (see protocol.ts). Keeps working offline: changes wait in the
 * outbox and go when the share can be reached. Nothing here ever blocks counting.
 */

export interface SyncConfig {
  key: string;
  /** The last share sequence number this device has pulled. */
  since: number;
  lastSyncAt: string | null;
  /** The `TABLES_VERSION` this device last pulled with (absent = 1). */
  tables?: number;
}

export type SyncState = 'off' | 'idle' | 'syncing' | 'offline' | 'error';

export interface SyncStatus {
  state: SyncState;
  /** Changes on this device not yet on the share. */
  pending: number;
  lastSyncAt: string | null;
  message: string | null;
}

/** How the engine reaches the share (the real one uses fetch; tests pass their own). */
export type Transport = (path: string, init: { method: 'GET' | 'POST'; key?: string; body?: unknown; headers?: Record<string, string> }) => Promise<Response>;

export const fetchTransport: Transport = (path, init) =>
  fetch(`/api/sync/${path}`, {
    method: init.method,
    headers: { 'content-type': 'application/json', ...(init.key ? { authorization: `Bearer ${init.key}` } : {}), ...init.headers },
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
    cache: 'no-store',
  });

export class SyncError extends Error {
  constructor(
    message: string,
    readonly offline = false,
  ) {
    super(message);
  }
}

const CONFIG_KEY = 'sync';
type Rec = Record<string, unknown>;

export class SyncEngine {
  private status: SyncStatus = { state: 'off', pending: 0, lastSyncAt: null, message: null };
  private listeners = new Set<() => void>();
  private running: Promise<void> | null = null;
  private again = false;
  private timer: ReturnType<typeof setInterval> | null = null;
  private debounce: ReturnType<typeof setTimeout> | null = null;
  private cleanup: (() => void) | null = null;

  constructor(
    readonly db: LastCallDB,
    private readonly transport: Transport = fetchTransport,
  ) {}

  // ------------------------------------------------------------------ status
  subscribe = (l: () => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };
  getStatus = () => this.status;
  private set(patch: Partial<SyncStatus>) {
    this.status = { ...this.status, ...patch };
    this.listeners.forEach((l) => l());
  }

  async config(): Promise<SyncConfig | null> {
    return ((await this.db.meta.get(CONFIG_KEY))?.value as SyncConfig | undefined) ?? null;
  }
  private async saveConfig(c: SyncConfig | null) {
    if (c) await this.db.meta.put({ key: CONFIG_KEY, value: c });
    else await this.db.meta.delete(CONFIG_KEY);
  }

  private async call<T>(path: string, init: Parameters<Transport>[1]): Promise<T> {
    let res: Response;
    try {
      res = await this.transport(path, init);
    } catch {
      throw new SyncError('Can’t reach the share right now (no internet?). Everything is saved here and will go when it can.', true);
    }
    const body = (await res.json().catch(() => ({}))) as T & { error?: string };
    if (!res.ok) throw new SyncError(body.error ?? `The share answered ${res.status}.`);
    return body;
  }

  // ------------------------------------------------------------------ turning it on/off
  /** First device: starts a new sharing group with everything on this device. */
  async startGroup(setupCode?: string): Promise<void> {
    const key = randomKey();
    await this.call('groups', { method: 'POST', key, headers: setupCode ? { 'x-setup-code': setupCode } : {} });
    await this.saveConfig({ key, since: 0, lastSyncAt: null, tables: TABLES_VERSION });
    await this.db.shadows.clear();
    await this.db.outbox.clear();
    await this.start();
  }

  /**
   * A fresh device joins with the other device's 8-letter code — or with the owner's
   * recovery code when no device is left — then gets everything.
   */
  async join(code: string): Promise<void> {
    const typed = normalizeCode(code);
    let key: string;
    if (typed.length === 8) key = (await this.call<{ key: string }>('join', { method: 'POST', body: { code: typed } })).key;
    else if (typed.length >= 30) {
      key = typed;
      await this.call('status', { method: 'GET', key }).catch((err: unknown) => {
        throw err instanceof SyncError && !err.offline ? new SyncError('That recovery code isn’t right. Check it letter by letter.') : err;
      });
    } else throw new SyncError('Type the 8-letter join code from the other device, or the owner’s recovery code.');
    await this.saveConfig({ key, since: 0, lastSyncAt: null, tables: TABLES_VERSION });
    await this.db.shadows.clear();
    await this.db.outbox.clear();
    await this.start();
  }

  /** The owner's recovery code (the group key), for joining when no device is left. */
  async recoveryCode(): Promise<string | null> {
    return (await this.config())?.key ?? null;
  }

  /** A one-time code another device can use to join (15 minutes). */
  async pairCode(): Promise<{ code: string; expiresAt: number }> {
    const c = await this.config();
    if (!c) throw new SyncError('Sharing isn’t on on this device.');
    return this.call('pair', { method: 'POST', key: c.key });
  }

  /** Stops sharing on this device. Its records stay; the other devices keep theirs. */
  async leave(): Promise<void> {
    this.stop();
    await this.saveConfig(null);
    await this.db.outbox.clear();
    await this.db.shadows.clear();
    this.set({ state: 'off', pending: 0, message: null });
  }

  /** Turns the engine on if this device shares. Safe to call again. */
  async start(): Promise<boolean> {
    let c = await this.config();
    if (!c) {
      this.set({ state: 'off' });
      return false;
    }
    // Updated to a version that shares a new kind of record: fetch everything once more, so
    // records of it skipped while on the old version arrive too (known ones are skipped quickly).
    if ((c.tables ?? 1) < TABLES_VERSION) {
      c = { ...c, since: 0, tables: TABLES_VERSION };
      await this.saveConfig(c);
    }
    this.db.syncOn = true;
    this.db.onLocalChange = () => this.soon();
    this.set({ state: 'idle', lastSyncAt: c.lastSyncAt });
    await this.reconcile();
    if (typeof window !== 'undefined' && !this.timer) {
      this.timer = setInterval(() => {
        if (document.visibilityState === 'visible') void this.syncNow();
      }, 10_000);
      const online = () => void this.syncNow();
      window.addEventListener('online', online);
      document.addEventListener('visibilitychange', online);
      this.cleanup = () => {
        window.removeEventListener('online', online);
        document.removeEventListener('visibilitychange', online);
      };
    }
    void this.syncNow();
    return true;
  }

  stop() {
    this.db.syncOn = false;
    this.db.onLocalChange = null;
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.cleanup?.();
    this.cleanup = null;
  }

  private soon() {
    if (this.debounce) clearTimeout(this.debounce);
    this.debounce = setTimeout(() => void this.syncNow(), 1500);
  }

  // ------------------------------------------------------------------ the work
  /**
   * Queues anything that differs from the share's last-seen copy: records saved before
   * sharing was on, or written outside the normal write path.
   */
  async reconcile(): Promise<number> {
    const queue: OutboxEntry[] = [];
    const at = Date.now();
    for (const tbl of SYNCED_TABLES) {
      const rows = (await this.db.table(tbl).toArray()) as Rec[];
      const shadows = new Map((await this.db.shadows.where('key').startsWith(`${tbl}|`).toArray()).map((s) => [s.key, s]));
      for (const r of rows) {
        const key = `${tbl}|${String(r.id)}`;
        const sh = shadows.get(key);
        shadows.delete(key);
        if (!sh || (tbl !== 'audit' && (sh.lv !== (r.version ?? null) || sh.lu !== (r.updatedAt ?? null)))) queue.push({ key, tbl, id: String(r.id), at });
      }
      // On the share but gone from here: deleted on this device.
      for (const sh of shadows.values()) if (sh.data != null) queue.push({ key: sh.key, tbl, id: sh.key.slice(tbl.length + 1), at });
    }
    if (queue.length) await this.db.outbox.bulkPut(queue);
    await this.countPending();
    return queue.length;
  }

  private async countPending() {
    this.set({ pending: await this.db.outbox.count() });
  }

  /** Sends and fetches until both sides agree. One run at a time. */
  syncNow(): Promise<void> {
    if (this.running) {
      this.again = true;
      return this.running;
    }
    this.running = (async () => {
      do {
        this.again = false;
        await this.runOnce();
      } while (this.again);
    })().finally(() => {
      this.running = null;
    });
    return this.running;
  }

  private async runOnce(): Promise<void> {
    const c = await this.config();
    if (!c || !this.db.syncOn) return;
    this.set({ state: 'syncing', message: null });
    try {
      for (let i = 0; i < 50 && (await this.pushBatch(c)); i++);
      await this.pull(c);
      for (let i = 0; i < 50 && (await this.pushBatch(c)); i++);
      const now = new Date().toISOString();
      await this.saveConfig({ ...c, since: (await this.config())?.since ?? c.since, lastSyncAt: now });
      await this.countPending();
      this.set({ state: 'idle', lastSyncAt: now, message: null });
    } catch (err) {
      await this.countPending();
      const offline = err instanceof SyncError && err.offline;
      this.set({ state: offline ? 'offline' : 'error', message: (err as Error).message });
    }
  }

  /** Sends up to 200 changes. Returns true when there may be more to send. */
  private async pushBatch(c: SyncConfig): Promise<boolean> {
    const entries = await this.db.outbox.orderBy('at').limit(MAX_PUSH).toArray();
    if (entries.length === 0) return false;
    const sent = await Promise.all(
      entries.map(async (e) => ({ e, rec: ((await this.db.table(e.tbl).get(e.id)) as Rec | undefined) ?? null, sh: await this.db.shadows.get(e.key) })),
    );
    // A record that never reached the share and is gone now: nothing to send.
    const live = sent.filter((s) => s.rec || s.sh);
    for (const s of sent) if (!s.rec && !s.sh) await this.dropIfUnchanged(s.e);
    if (live.length === 0) return entries.length === MAX_PUSH;
    const changes: ChangeIn[] = live.map(({ e, rec, sh }) => ({ tbl: e.tbl as ChangeIn['tbl'], id: e.id, base: sh?.v ?? null, data: rec }));
    const { results } = await this.call<PushResponse>('push', { method: 'POST', key: c.key, body: { changes } });
    for (let i = 0; i < live.length; i++) {
      const { e, rec, sh } = live[i]!;
      const r = results[i];
      if (!r) continue;
      if (r.ok) {
        await this.db.transaction('rw', ['shadows'], () => this.db.shadows.put(shadowOf(e.key, e.tbl, r.v, rec)));
        await this.dropIfUnchanged(e);
      } else if (!r.current) {
        // The share has no copy any more: send it again as new.
        await this.db.shadows.delete(e.key);
      } else {
        await this.absorb(e.tbl, e.id, sh, rec, r.current.v, r.current.data, e);
      }
    }
    return true;
  }

  private async dropIfUnchanged(e: OutboxEntry) {
    await this.db.transaction('rw', ['outbox'], async () => {
      const cur = await this.db.outbox.get(e.key);
      if (cur && cur.at === e.at) await this.db.outbox.delete(e.key);
    });
  }

  /**
   * The share has a newer copy than the one this device's change was based on (or a new
   * record arrived while this device had its own change waiting): merge them.
   */
  private async absorb(tbl: string, id: string, sh: Shadow | undefined, mine: Rec | null, v: number, theirs: Rec | null, pending: OutboxEntry) {
    const raw = tbl === 'audit' ? (mine ?? theirs) : (merge3(sh?.data ?? null, mine, theirs) as Rec | null);
    const sameAsShare = deepEqual(raw, theirs);
    const result = sameAsShare ? theirs : tbl === 'audit' ? raw : mergeRecord(sh?.data, mine, theirs);
    await this.db.transaction('rw', [tbl, 'shadows'], async () => {
      if (result) await this.db.table(tbl).put(result);
      else await this.db.table(tbl).delete(id);
      await this.db.shadows.put(sameAsShare ? shadowOf(`${tbl}|${id}`, tbl, v, theirs) : { key: `${tbl}|${id}`, v, data: tbl === 'audit' ? null : theirs, lv: null, lu: null });
    });
    if (sameAsShare) await this.dropIfUnchanged(pending);
  }

  private async pull(c: SyncConfig): Promise<void> {
    let since = c.since;
    for (let round = 0; round < 1000; round++) {
      const res = await this.call<PullResponse>(`pull?since=${since}`, { method: 'GET', key: c.key });
      for (const r of res.records) {
        if (!isSyncedTable(r.tbl)) continue;
        const key = `${r.tbl}|${r.id}`;
        const pending = await this.db.outbox.get(key);
        // Already have exactly this (usually this device's own change coming back).
        if (!pending && (await this.db.shadows.get(key))?.v === r.v) continue;
        if (pending) {
          const mine = ((await this.db.table(r.tbl).get(r.id)) as Rec | undefined) ?? null;
          await this.absorb(r.tbl, r.id, await this.db.shadows.get(key), mine, r.v, r.data, pending);
        } else {
          await this.db.transaction('rw', [r.tbl, 'shadows'], async () => {
            if (r.data) await this.db.table(r.tbl).put(r.data);
            else await this.db.table(r.tbl).delete(r.id);
            await this.db.shadows.put(shadowOf(key, r.tbl, r.v, r.data));
          });
        }
      }
      since = res.next;
      const cur = await this.config();
      if (cur) await this.saveConfig({ ...cur, since });
      if (!res.more) break;
    }
  }
}

function shadowOf(key: string, tbl: string, v: number, rec: Rec | null): Shadow {
  return { key, v, data: tbl === 'audit' ? null : rec, lv: (rec?.version as number | undefined) ?? null, lu: (rec?.updatedAt as string | undefined) ?? null };
}

// ---------------------------------------------------------------------------
// One engine per open storage
// ---------------------------------------------------------------------------

let engine: SyncEngine | null = null;

export function syncEngineFor(db: LastCallDB, transport?: Transport): SyncEngine {
  if (!engine || engine.db !== db) {
    engine?.stop();
    engine = new SyncEngine(db, transport);
  }
  return engine;
}

export function currentSyncEngine(): SyncEngine | null {
  return engine;
}

const OFF: SyncStatus = { state: 'off', pending: 0, lastSyncAt: null, message: null };

export function useSyncStatus(): SyncStatus {
  const e = engine;
  return useSyncExternalStore(e ? e.subscribe : () => () => {}, e ? e.getStatus : () => OFF, () => OFF);
}
